import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomDialog from 'components/Dialog/Dialog';
import { RHFTextFieldOutlined } from 'components/RHF';
import * as yup from 'yup';
import { useVerifyDailyReconciliation } from '../../hooks/mutations/useReconciliationMutations';
import moment from 'moment';

interface AuditVerificationDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit?: (data: { reason: string }) => void;
    storeId?: string;
    date?: Date | null;
    mode?: 'verify' | 'ack';
    existingNote?: string;
    isVerified?: boolean;
    isAck?: boolean;
    managerNote?: string;
    cashierShiftNotes?: Array<Record<string, any>>;
}

interface AuditVerificationFormValues {
    reason: string;
}

const auditVerificationSchema = yup.object().shape({
    reason: yup.string().required('Notes/Reason is required')
});

const AuditVerificationDialog: React.FC<AuditVerificationDialogProps> = ({
    open,
    onClose,
    onSubmit,
    storeId,
    date,
    mode = 'verify',
    existingNote = '',
    isVerified,
    isAck,
    managerNote = '',
    cashierShiftNotes = []
}) => {
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const verifyMutation = useVerifyDailyReconciliation(storeId || '');

    const methods = useForm<AuditVerificationFormValues>({
        resolver: yupResolver(auditVerificationSchema),
        defaultValues: {
            reason: ''
        },
        mode: 'onChange'
    });

    const { handleSubmit, reset } = methods;

    // Reset form when dialog opens/closes
    useEffect(() => {
        if (open) {
            reset({
                reason: existingNote || ''
            });
            setError(null);
        } else {
            reset({
                reason: ''
            });
            setError(null);
        }
    }, [open, reset, existingNote]);

    const handleFormSubmit = useCallback(async (data: AuditVerificationFormValues) => {
        try {
            setError(null);
            setIsSubmitting(true);

            if (!storeId || !date) {
                throw new Error('Store ID and date are required');
            }

            const formattedDate = moment(date).format('YYYY-MM-DD');
            const payload = {
                reconciliation_date: formattedDate,
                verification_note: data.reason
            };

            const result = await verifyMutation.mutateAsync(payload);

            if (result && result.success) {
                // Call custom onSubmit if provided (for backward compatibility)
                if (onSubmit) {
                    await onSubmit(data);
                }

                // Reset form and close
                reset({
                    reason: ''
                });
                onClose();
            } else {
                setError(result?.error || 'Failed to verify daily reconciliation');
            }
        } catch (err: any) {
            console.error('Error submitting audit verification:', err);
            setError(err.message || 'Error submitting audit verification');
        } finally {
            setIsSubmitting(false);
        }
    }, [onSubmit, onClose, reset, storeId, date, verifyMutation]);

    return (
        <CustomDialog
            open={open}
            handleClose={onClose}
            handleSubmit={handleSubmit(handleFormSubmit)}
            title="Verify Daily Reconciliation"
            buttonTxt="Verify"
            cancelBtn={true}
            cancelBtnTxt="Cancel"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            sx={{
                "& .MuiPaper-root": {
                    minWidth: "580px",
                }
            }}
        >
            <FormProvider {...methods}>
                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {error && (
                        <Typography sx={{ color: 'error.main', fontSize: '14px' }}>
                            {error}
                        </Typography>
                    )}
                    <Box component="section" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {managerNote ? (
                            <Box sx={{ p: 1.5, bgcolor: '#F6F8FB', border: '1px solid #E4E8F0', borderRadius: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#4F5E72', mb: 0.5 }}>
                                    Manager Note
                                </Typography>
                                <Typography sx={{ fontSize: '13px', color: '#1f2937', whiteSpace: 'pre-wrap' }}>
                                    {managerNote}
                                </Typography>
                            </Box>
                        ) : null}
                        {cashierShiftNotes.length > 0 ? (
                            <Box sx={{ p: 1.5, bgcolor: '#F6F8FB', border: '1px solid #E4E8F0', borderRadius: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#4F5E72', mb: 0.5 }}>
                                    Cashier Shift Notes
                                </Typography>
                                {cashierShiftNotes.map((note, index) => (
                                    <Typography key={`${note.note || note.created_at || index}`} sx={{ fontSize: '13px', color: '#1f2937', whiteSpace: 'pre-wrap', mb: index === cashierShiftNotes.length - 1 ? 0 : 0.75 }}>
                                        {String(note.note || note.text || note.message || '')}
                                    </Typography>
                                ))}
                            </Box>
                        ) : null}
                        <Typography
                            sx={{
                                fontFamily: 'Inter',
                                fontWeight: 600,
                                fontSize: '16px',
                                color: '#1b3e61'
                            }}
                        >
                            Verification Note
                        </Typography>
                        <RHFTextFieldOutlined
                            name="reason"
                            placeholder="Describe the reconciliation review..."
                            multiline
                            minRows={5}
                            fullWidth
                            disabled={isSubmitting}
                            sx={{
                                '& .MuiInputBase-root': {
                                    padding: '8px !important',
                                }
                            }}
                        />
                    </Box>
                </Box>
            </FormProvider>
        </CustomDialog>
    );
};

export default AuditVerificationDialog;
